import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { BASE_API_PATH, FEATURED_COLLECTION_ID, PHOTOS_PATH } from '../constants';

interface FeaturedImage {
    id: string;
    imageUrl: string;
    caption: string;
    sortOrder: number;
    focalPointX?: number;
    focalPointY?: number;
}

interface FeaturedCollectionResponse {
    id: string;
    title: string;
    description: string;
    photoCount: number;
    publishedAt: number;
    galleryImages: FeaturedImage[];
}

function formatCollectionDate(timestamp: number) {
    if (!timestamp) {
        return 'Unknown date';
    }
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}

export function FeaturedPhotos() {
    const [collection, setCollection] = useState<FeaturedCollectionResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        document.title = 'Featured Photos - Mike Brauninger';
        ReactGA.send({ hitType: 'pageview', page: window.location.pathname });
    }, []);

    useEffect(() => {
        const fetchFeaturedCollection = async () => {
            try {
                const response = await fetch(`${BASE_API_PATH}/photos/${FEATURED_COLLECTION_ID}`);
                if (!response.ok) {
                    throw new Error('Could not fetch featured photos');
                }
                const data: FeaturedCollectionResponse = await response.json();
                setCollection(data);
                setError(false);
            } catch (fetchError) {
                setError(true);
            } finally {
                setLoading(false);
            }
        };
        fetchFeaturedCollection();
    }, []);

    const sortedImages = useMemo(() => {
        if (!collection) {
            return [];
        }
        return [...collection.galleryImages].sort((left, right) => left.sortOrder - right.sortOrder);
    }, [collection]);

    return (
        <>
            <Helmet>
                <title>Featured Photos - Mike Brauninger</title>
            </Helmet>
            <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 4, md: 6 } }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    <Box sx={{ pb: 3, borderBottom: '1px solid #d9e7f8', mb: 2.5 }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.8}
                            sx={{
                                color: '#4d6f9f',
                                fontSize: 14,
                                mb: 1.25,
                                fontFamily: (theme) => theme.typography.fontFamily,
                            }}
                        >
                            <Link
                                to={`/${PHOTOS_PATH}`}
                                style={{
                                    textDecoration: 'none',
                                    color: '#2d5ea8',
                                    display: 'flex',
                                    fontWeight: 500,
                                }}
                            >
                                <ArrowBackIcon sx={{ fontSize: 15, mr: 0.5 }} />
                                <span>All Collections</span>
                            </Link>
                            <Typography component="span" sx={{ color: '#7f98be', fontSize: 14 }}>
                                /
                            </Typography>
                            <Typography
                                component="span"
                                aria-current="page"
                                sx={{ color: '#163f86', fontSize: 14, fontWeight: 700 }}
                            >
                                Featured Photos
                            </Typography>
                        </Stack>
                        <Typography variant="h3" sx={{ color: '#163f86', fontWeight: 700, mb: 0.5 }}>
                            Featured Photos
                        </Typography>
                        <Typography sx={{ color: '#55729f', maxWidth: 660 }}>
                            {collection?.description || 'Some of my favorites 😎'}
                        </Typography>
                        {collection && (
                            <Stack direction="row" spacing={{ xs: 2, md: 3 }} sx={{ flexWrap: 'wrap', rowGap: 1.5, mt: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CollectionsOutlinedIcon sx={{ color: '#2d66c3', fontSize: 21 }} />
                                    <Box>
                                        <Typography sx={{ color: '#1f4f9c', fontWeight: 700, fontSize: 15 }}>
                                            {collection.photoCount}
                                        </Typography>
                                        <Typography sx={{ color: '#55729f', fontSize: 13 }}>Photos</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TodayOutlinedIcon sx={{ color: '#2d66c3', fontSize: 21 }} />
                                    <Box>
                                        <Typography sx={{ color: '#1f4f9c', fontWeight: 700, fontSize: 15 }}>
                                            {formatCollectionDate(collection.publishedAt)}
                                        </Typography>
                                        <Typography sx={{ color: '#55729f', fontSize: 13 }}>Last Updated</Typography>
                                    </Box>
                                </Box>
                            </Stack>
                        )}
                    </Box>

                    {loading ? (
                        <Typography sx={{ color: '#55729f' }}>Loading featured photos...</Typography>
                    ) : error || !collection ? (
                        <Typography sx={{ color: '#55729f' }}>Unable to load featured photos right now.</Typography>
                    ) : (
                        <Grid container spacing={1.5}>
                            {sortedImages.map((image) => (
                                <Grid item xs={12} sm={6} md={4} key={image.id}>
                                    <Box>
                                        <Box
                                            component="img"
                                            src={image.imageUrl}
                                            alt={image.caption || 'Featured photo'}
                                            sx={{
                                                width: '100%',
                                                borderRadius: 2,
                                                aspectRatio: '16 / 10',
                                                objectFit: 'cover',
                                                objectPosition: `${image.focalPointX ?? 50}% ${image.focalPointY ?? 50}%`,
                                            }}
                                        />
                                        {image.caption && (
                                            <Typography sx={{ color: '#55729f', mt: 0.5, fontSize: 13.5 }}>
                                                {image.caption}
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
            </Box>
        </>
    );
}
